/* Nassau Heritage — AJAX add to cart + slide-out cart drawer (2026-09-30).
   Loaded after theme.js. Leaves theme.js untouched: the product form still
   works as a normal POST when JS is off, and the header cart icon still links
   to /cart. */
(() => {
  'use strict';

  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  const panel = $('[data-drawer-panel]', drawer);
  const itemsEl = $('[data-drawer-items]', drawer);
  const emptyEl = $('[data-drawer-empty]', drawer);
  const footEl = $('[data-drawer-foot]', drawer);
  const subtotalEl = $('[data-drawer-subtotal]', drawer);
  const countEl = $('[data-drawer-count]', drawer);
  const toast = $('[data-toast]');
  const routes = (window.NHC && window.NHC.routes) || { cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' };
  const moneyFormat = (window.NHC && window.NHC.moneyFormat) || '${{amount}}';

  let lastFocus = null;
  let busy = false;

  // ---- helpers -----------------------------------------------------------

  const escapeHTML = (v) => String(v ?? '').replace(/[&<>'"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[c]));

  // Minimal Shopify money formatter: handles the common {{amount}} variants.
  const formatMoney = (cents) => {
    const n = Number(cents || 0) / 100;
    const withCommas = (v, dec) => v.toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withCommas(n, 0).replace(/,/g, '.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, n.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas(n, 0))
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas(n, 2));
  };

  const imgSrc = (url, w) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${w}` : '');

  const showToast = (msg) => {
    if (!toast) return;
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(showToast.t);
    showToast.t = setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  const setBadge = (count) => {
    $$('.cart-count').forEach((el) => { el.textContent = count; });
    $$('.cart-link').forEach((el) => el.setAttribute('aria-label', `Cart with ${count} items`));
    if (countEl) countEl.textContent = count;
  };

  // ---- rendering ---------------------------------------------------------

  const renderItem = (item, index) => {
    const opts = item.product_has_only_default_variant ? '' : `<div class="cart-drawer__variant">${escapeHTML(item.variant_title)}</div>`;
    const props = Object.entries(item.properties || {})
      .filter(([k, v]) => v && !k.startsWith('_'))
      .map(([k, v]) => `<div class="cart-drawer__variant">${escapeHTML(k)}: ${escapeHTML(v)}</div>`).join('');
    const img = item.image ? `<a class="cart-drawer__thumb" href="${escapeHTML(item.url)}"><img src="${escapeHTML(imgSrc(item.image, 160))}" alt="${escapeHTML(item.product_title)}" width="80" height="80" loading="lazy"></a>` : '<div class="cart-drawer__thumb"></div>';
    return `
      <div class="cart-drawer__item" data-line="${index + 1}" data-key="${escapeHTML(item.key)}">
        ${img}
        <div class="cart-drawer__info">
          <a class="cart-drawer__name" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
          ${opts}${props}
          <div class="cart-drawer__controls">
            <div class="cart-drawer__qty" role="group" aria-label="Quantity for ${escapeHTML(item.product_title)}">
              <button type="button" data-qty-delta="-1" aria-label="Decrease quantity">&minus;</button>
              <input type="number" min="0" inputmode="numeric" value="${item.quantity}" data-qty-input aria-label="Quantity">
              <button type="button" data-qty-delta="1" aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="cart-drawer__remove" data-remove>Remove</button>
          </div>
        </div>
        <div class="cart-drawer__price">${formatMoney(item.final_line_price)}</div>
      </div>`;
  };

  const render = (cart) => {
    const has = cart.item_count > 0;
    itemsEl.innerHTML = has ? cart.items.map(renderItem).join('') : '';
    emptyEl.hidden = has;
    footEl.hidden = !has;
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    setBadge(cart.item_count);
    // 2026-10-09: let the product page know what is in the cart so its
    // quantity cap can subtract what is already there.
    window.NHC = window.NHC || {};
    window.NHC.cart = cart;
    document.dispatchEvent(new CustomEvent('nhc:cart', { detail: cart }));
  };

  // ---- cart API ----------------------------------------------------------

  const getCart = async () => {
    const r = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('cart');
    return r.json();
  };

  const refresh = async () => {
    try { render(await getCart()); } catch (_) { /* leave as is */ }
  };

  // Changes are keyed by line-item key (stable) rather than positional line
  // number, which can shift if a row is removed while another request is
  // still in flight.
  const changeLine = async (key, quantity) => {
    if (busy) return;
    busy = true;
    drawer.classList.add('is-busy');
    try {
      const r = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const cart = await r.json();
      if (!r.ok) throw new Error(cart.description || cart.message || 'Could not update cart');
      render(cart);
    } catch (e) {
      showToast(e.message || 'Could not update cart');
      refresh();
    } finally {
      busy = false;
      drawer.classList.remove('is-busy');
    }
  };

  // ---- open / close ------------------------------------------------------

  const open = async () => {
    lastFocus = document.activeElement;
    await refresh();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    requestAnimationFrame(() => panel.focus());
  };

  const close = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  };

  $$('[data-drawer-close]', drawer).forEach((el) => el.addEventListener('click', close));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });

  // Header cart icons open the drawer (href stays as the no-JS fallback).
  $$('.cart-link, .announcement-cart').forEach((a) => a.addEventListener('click', (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    open();
  }));

  // Drawer line controls (delegated).
  itemsEl.addEventListener('click', (e) => {
    const row = e.target.closest('[data-key]');
    if (!row) return;
    const key = row.dataset.key;
    const input = $('[data-qty-input]', row);
    if (e.target.closest('[data-remove]')) return changeLine(key, 0);
    const delta = e.target.closest('[data-qty-delta]');
    if (delta) return changeLine(key, Math.max(0, Number(input.value || 0) + Number(delta.dataset.qtyDelta)));
  });
  itemsEl.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    const row = input.closest('[data-key]');
    changeLine(row.dataset.key, Math.max(0, Number(input.value || 0)));
  });

  // ---- AJAX add from the product form -----------------------------------

  $$('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const button = $('[data-add-to-cart]', form);
      if (busy || (button && button.disabled)) return;
      busy = true;
      const label = button ? button.textContent : '';
      if (button) { button.disabled = true; button.textContent = 'Adding…'; }
      try {
        const fd = new FormData(form);
        const r = await fetch(`${routes.cartAdd}.js`, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
        const data = await r.json();
        if (!r.ok) throw new Error(data.description || data.message || 'Could not add to cart');
        if (button) { button.textContent = 'Added ✓'; }
        busy = false;
        await open();
      } catch (err) {
        showToast(err.message || 'Could not add to cart');
      } finally {
        busy = false;
        if (button) {
          setTimeout(() => {
            button.textContent = label;
            // The product page may have decided the button should stay off
            // (everything in stock is now in the cart); respect that.
            button.disabled = button.dataset.capLocked === 'true';
            document.dispatchEvent(new CustomEvent('nhc:cart-settled'));
          }, 900);
        }
      }
    });
  });

  // Prime the product page's cap with the current cart on load.
  refresh();
})();
